<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import VueHCaptcha from '@hcaptcha/vue3-hcaptcha'
import api from '@/services/api'
import { useHCaptcha } from '@/composables/useHCaptcha'
import HCaptchaNotice from '@/components/HCaptchaNotice.vue'

const router = useRouter()
const { hCaptchaRef, siteKey, execute: executeHCaptcha } = useHCaptcha()

const email = ref('')
const errors = ref<Record<string, string[]>>({})
const inFlight = ref(false)

async function handleSubmit() {
  errors.value = {}
  inFlight.value = true

  const { token, error } = await executeHCaptcha()
  if (error) {
    errors.value = {
      hcaptcha_token: ['Security verification failed. Please try again.'],
    }
    inFlight.value = false
    return
  }

  try {
    await api.post('/password-reset/', {
      email: email.value,
      hcaptcha_token: token,
    })
    router.push({ name: 'password-reset-done' })
  } catch (error: unknown) {
    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as {
        response?: { data?: Record<string, string[]> }
      }
      errors.value = axiosError.response?.data ?? {}
    }
  } finally {
    inFlight.value = false
  }
}
</script>

<template>
  <main class="col mt-3">
    <div class="row">
      <div class="col-md-6 offset-md-3">
        <h3>Reset Your Password</h3>
        <p>
          Forgot your password for Stadia? No problem. Just enter your email
          address below and we'll send you a link to reset it.
        </p>

        <form @submit.prevent="handleSubmit" id="password-reset-form">
          <div class="mb-3">
            <label for="email" class="form-label">Email address</label>
            <input
              type="email"
              class="form-control"
              id="email"
              v-model="email"
              required
              :class="{ 'is-invalid': errors.email }"
            />
            <div class="invalid-feedback" v-if="errors.email">
              {{ errors.email[0] }}
            </div>
          </div>

          <div class="mb-3">
            <vue-h-captcha
              ref="hCaptchaRef"
              size="invisible"
              :sitekey="siteKey"
            />
          </div>

          <button type="submit" class="btn btn-primary" :disabled="inFlight">
            <span
              v-if="inFlight"
              class="spinner-border spinner-border-sm"
              role="status"
            />
            <template v-else>Reset your password</template>
          </button>
        </form>

        <hr />

        <ul>
          <li>
            Please check your spam folder if you don't receive an email soon.
          </li>
          <li>
            If the account does not exist, no email will be sent and no error
            will be presented.
          </li>
        </ul>
        <HCaptchaNotice />
      </div>
    </div>
  </main>
</template>

<style scoped lang="scss"></style>
